import { Link } from 'react-router-dom';

interface Comic {
  id: number;
  slug: string;
  title: string;
  cover: string;
  rating: number;
  views: string;
  status: string;
  tags: string[];
}

interface Props {
  hotComics: Comic[];
}

export default function HotRecommendSection({ hotComics }: Props) {
  const mainComic = hotComics[0];
  const topComics = hotComics.slice(1, 3);

  return (
    <section className="py-16 md:py-20 bg-background-100">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        {/* Title */}
        <div className="text-center mb-12">
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 leading-tight" style={{ fontFamily: "var(--font-heading)" }}>
            本周<span className="text-accent-500">最热</span>推荐
          </h2>
          <p className="text-sm md:text-base text-foreground-500 mt-3">
            千万漫迷的选择，不容错过的精品漫画
          </p>
        </div>

        {/* Irregular grid */}
        <div className="flex flex-col lg:flex-row gap-4 md:gap-5">
          {/* Left: Main featured comic */}
          <Link
            to={`/article/${mainComic.slug}`}
            className="relative lg:w-1/2 h-[420px] md:h-[500px] rounded-xl overflow-hidden group cursor-pointer"
          >
            <img
              src={mainComic.cover}
              alt={mainComic.title}
              className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>
            <div className="absolute bottom-0 left-0 right-0 p-5 md:p-6">
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                {mainComic.tags.map((tag) => (
                  <span key={tag} className="px-2.5 py-1 rounded-full bg-white/20 text-white text-xs whitespace-nowrap">
                    {tag}
                  </span>
                ))}
              </div>
              <h3 className="text-xl md:text-2xl font-bold text-white mb-2">{mainComic.title}</h3>
              <div className="flex items-center gap-4 text-white/70 text-sm">
                <span className="flex items-center gap-1">
                  <i className="ri-star-fill text-accent-500 text-sm w-4 h-4 flex items-center justify-center"></i>
                  {mainComic.rating}
                </span>
                <span>{mainComic.views} 阅读</span>
                <span className="text-accent-400">{mainComic.status}</span>
              </div>
            </div>
          </Link>

          {/* Right: Two stacked cards */}
          <div className="lg:w-1/2 flex flex-col gap-4 md:gap-5">
            {topComics.map((comic, index) => (
              <Link
                key={comic.id}
                to={`/article/${comic.slug}`}
                className="relative h-[200px] md:h-[240px] rounded-xl overflow-hidden group cursor-pointer"
              >
                <img
                  src={comic.cover}
                  alt={comic.title}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/25 to-transparent"></div>
                <div className="absolute bottom-0 left-0 right-0 p-4 md:p-5">
                  <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                    {comic.tags.slice(0, 2).map((tag) => (
                      <span key={tag} className="px-2 py-0.5 rounded-full bg-white/15 text-white text-xs whitespace-nowrap">
                        {tag}
                      </span>
                    ))}
                  </div>
                  <h3 className="text-lg md:text-xl font-bold text-white">{comic.title}</h3>
                  <div className="flex items-center gap-3 text-white/60 text-xs mt-1.5">
                    <span className="flex items-center gap-1">
                      <i className="ri-star-fill text-accent-500 text-xs w-3 h-3 flex items-center justify-center"></i>
                      {comic.rating}
                    </span>
                    <span>{comic.views}</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}